import React, { useCallback, useMemo } from 'react';
import { TableCell, TableRow, Typography, alpha } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import type { OnChangeFn, PaginationState, SortingState, VisibilityState } from '@tanstack/react-table';
import { ColumnFilter } from '../filters/ColumnFilter';
import TanStackDataTable from '../table/TanStackDataTable';
import { utilizationPrimaryHeaderCellSx } from '../table/DataTable';
import { SEARCE_COLORS } from '../../theme';
import type { BillingBudget, BillingBudgetField } from '../../types/billing';
import { BILLING_FILTERABLE_COLUMNS, getBillingColumnLabel } from '../../utils/billingTableConfig';
import {
  compareBillingRows,
  getUniqueBillingFilterValues,
  type BillingColumnFilterMenuState,
} from '../../utils/billingTableFilters';
import {
  buildSandboxBillingDataTableColumns,
  getSandboxBillingSortableColumnIds,
} from './sandboxBillingDataTableColumns';
import { SandboxBillingGroupedTableHead } from './sandboxBillingGroupedTableHead';

/** Solid primary on hover — no fade from shared utilization header style. */
const sandboxBillingHeaderCellSx: SxProps<Theme> = {
  ...utilizationPrimaryHeaderCellSx,
  '&:hover': {
    backgroundColor: SEARCE_COLORS.primary,
  },
};

type ActiveCell = { accountId: string; field: BillingBudgetField } | null;

export interface SandboxBillingTableProps {
  columnVisibility: VisibilityState;
  onColumnVisibilityChange: OnChangeFn<VisibilityState>;
  rows: BillingBudget[];
  allRowsForFilters: BillingBudget[];
  isLoading: boolean;
  sorting: SortingState;
  onSortingChange: React.Dispatch<React.SetStateAction<SortingState>>;
  pagination: PaginationState;
  onPaginationChange: React.Dispatch<React.SetStateAction<PaginationState>>;
  filterMenus: Record<string, BillingColumnFilterMenuState>;
  onFilterClick: (columnId: BillingBudgetField, event: React.MouseEvent<HTMLElement>) => void;
  onFilterClose: (columnId: string) => void;
  onFilterSelect: (columnId: string, selectedValues: string[]) => void;
  canEdit: boolean;
  editableFields: Set<BillingBudgetField>;
  activeCell: ActiveCell;
  draftValue: string;
  onDraftChange: (value: string) => void;
  onCellDoubleClick: (row: BillingBudget, field: BillingBudgetField) => void;
  onCommitActiveCell: () => void;
  onCancelActiveCell: () => void;
  getDisplayRow: (row: BillingBudget) => BillingBudget;
  isCellDirty: (accountId: string, field: BillingBudgetField) => boolean;
  onClearFilters?: () => void;
  editMode?: boolean;
  isRowSelected?: (accountId: string) => boolean;
  onToggleRowSelect?: (accountId: string) => void;
  businessUnitNames: string[];
  getSubBuNames: (businessUnit: string | null | undefined) => string[];
  onDropdownCommit: (row: BillingBudget, field: BillingBudgetField, value: string) => void;
}

export function SandboxBillingTable({
  columnVisibility,
  onColumnVisibilityChange,
  rows,
  allRowsForFilters,
  isLoading,
  sorting,
  onSortingChange,
  pagination,
  onPaginationChange,
  filterMenus,
  onFilterClick,
  onFilterClose,
  onFilterSelect,
  canEdit,
  editableFields,
  activeCell,
  draftValue,
  onDraftChange,
  onCellDoubleClick,
  onCommitActiveCell,
  onCancelActiveCell,
  getDisplayRow,
  isCellDirty,
  onClearFilters,
  editMode = false,
  isRowSelected,
  onToggleRowSelect,
  businessUnitNames,
  getSubBuNames,
  onDropdownCommit,
}: SandboxBillingTableProps): React.ReactElement {
  const isFilterActive = useCallback(
    (columnId: BillingBudgetField) =>
      (filterMenus[columnId]?.selectedValues?.length ?? 0) > 0,
    [filterMenus],
  );

  const handleHeaderSortClick = useCallback(
    (columnId: BillingBudgetField) => {
      onSortingChange((prev) => {
        const cur = prev[0];
        if (cur?.id === columnId) return [{ id: columnId, desc: !cur.desc }];
        return [{ id: columnId, desc: false }];
      });
      onPaginationChange((p) => ({ ...p, pageIndex: 0 }));
    },
    [onSortingChange, onPaginationChange],
  );

  const dataTableColumns = useMemo(
    () =>
      buildSandboxBillingDataTableColumns({
        sorting,
        isFilterActive,
        onFilterClick,
        onHeaderSortClick: handleHeaderSortClick,
        canEdit,
        editableFields,
        activeCell,
        draftValue,
        onDraftChange,
        onCommitActiveCell,
        onCancelActiveCell,
        onCellDoubleClick,
        getDisplayRow,
        isCellDirty,
        editMode,
        isRowSelected,
        onToggleRowSelect,
        businessUnitNames,
        getSubBuNames,
        onDropdownCommit,
      }),
    [
      sorting,
      isFilterActive,
      onFilterClick,
      handleHeaderSortClick,
      canEdit,
      editableFields,
      activeCell,
      draftValue,
      onDraftChange,
      onCommitActiveCell,
      onCancelActiveCell,
      onCellDoubleClick,
      getDisplayRow,
      isCellDirty,
      editMode,
      isRowSelected,
      onToggleRowSelect,
      businessUnitNames,
      getSubBuNames,
      onDropdownCommit,
    ],
  );

  const visibleColumns = useMemo(
    () => dataTableColumns.filter((c) => columnVisibility[c.id] !== false),
    [dataTableColumns, columnVisibility],
  );

  const customTableHead = useMemo(
    () => (
      <SandboxBillingGroupedTableHead
        columns={visibleColumns}
        headerCellBaseSx={sandboxBillingHeaderCellSx}
        sorting={sorting}
        onHeaderSortClick={handleHeaderSortClick}
        onFilterClick={onFilterClick}
        isFilterActive={isFilterActive}
      />
    ),
    [visibleColumns, sorting, handleHeaderSortClick, onFilterClick, isFilterActive],
  );

  const compareRows = useCallback(
    (a: BillingBudget, b: BillingBudget, columnId: string) =>
      compareBillingRows(a, b, columnId as BillingBudgetField),
    [],
  );

  const resolveBodyRowSx = useCallback(
    ({ row }: { row: BillingBudget }) =>
      row.is_flagged
        ? { borderLeft: `3px solid ${SEARCE_COLORS.warning}` }
        : {},
    [],
  );

  return (
    <>
      <TanStackDataTable<BillingBudget>
        data={rows}
        dataTableColumns={dataTableColumns}
        getRowId={(row) => row.account_id}
        sorting={sorting}
        onSortingChange={onSortingChange}
        pagination={pagination}
        onPaginationChange={onPaginationChange}
        sortableColumnIds={getSandboxBillingSortableColumnIds()}
        compareRows={compareRows}
        loading={isLoading}
        empty={!isLoading && rows.length === 0}
        emptyMessage="No accounts match your filters."
        maxHeight="100%"
        rowsPerPageOptions={[10, 25, 50, 100]}
        elevation={0}
        headerCellBaseSx={sandboxBillingHeaderCellSx}
        customTableHead={customTableHead}
        enableColumnResizing={false}
        enableColumnVisibility
        hideColumnVisibilityMenu
        columnVisibility={columnVisibility}
        onColumnVisibilityChange={onColumnVisibilityChange}
        rowHover
        resolveBodyRowSx={resolveBodyRowSx}
        tableSx={{
          width: 'max-content',
          minWidth: '100%',
          tableLayout: 'auto',
          '& .MuiTableCell-root': { borderRight: 'none', borderLeft: 'none' },
        }}
        paperSx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          border: `1px solid ${alpha(SEARCE_COLORS.primary, 0.12)}`,
          borderRadius: 2,
        }}
        tableContainerSx={{ flex: 1 }}
        renderEmptyBody={(visibleColumnCount) => (
          <TableRow>
            <TableCell colSpan={visibleColumnCount} align="center" sx={{ py: 4 }}>
              <Typography color="text.secondary" gutterBottom>
                No accounts match your filters.
              </Typography>
              {onClearFilters && (
                <Typography
                  component="button"
                  variant="body2"
                  onClick={onClearFilters}
                  sx={{
                    border: 'none',
                    background: 'none',
                    color: SEARCE_COLORS.primary,
                    cursor: 'pointer',
                    textDecoration: 'underline',
                  }}
                >
                  Clear all filters
                </Typography>
              )}
            </TableCell>
          </TableRow>
        )}
      />

      {BILLING_FILTERABLE_COLUMNS.map((columnId) => {
        const menu = filterMenus[columnId];
        return (
          <ColumnFilter
            key={columnId}
            anchorEl={menu?.anchorEl ?? null}
            open={Boolean(menu?.anchorEl)}
            onClose={() => onFilterClose(columnId)}
            onSelect={(values) => onFilterSelect(columnId, values)}
            options={getUniqueBillingFilterValues(allRowsForFilters, columnId)}
            selectedValues={menu?.selectedValues ?? []}
            title={getBillingColumnLabel(columnId)}
            enableSearch
          />
        );
      })}
    </>
  );
}
